import { useApolloClient } from '@apollo/client';
import { SaveCancelWidget } from 'components';
import { EndpointActionTypes, EndpointContext } from 'contexts/EndpointContext';
import { ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import type { ActionButtonProps } from 'contracts/interfaces/ActionButtonProps';
import { isEqual } from 'lodash';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GetEndpointDocument, useUpdateEndpointSchemaMutation } from 'services/graphql/generated/hooks';
import { getParams } from 'utils';

export const EndpointActionButtons: FC<ActionButtonProps> = ({ isEditable }) => {
  const client = useApolloClient();
  const ctx = useContext(EndpointContext);
  const { pathname } = useLocation();
  const params = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setIsSaved(false);
  }, [ctx?.state]);

  const endpointData = client.readQuery({
    query: GetEndpointDocument,
    variables: {
      uuid: params.entityId,
      packageVersionUuid: params.versionUuid
    }
  });

  /* TODO: Handle the error from this operation. Ticket for the implementation:
     https://deitycommerce.atlassian.net/browse/DI-564 */
  const [updateEndpointSchema, { loading: isSaving, error: saveError }] = useUpdateEndpointSchemaMutation({
    refetchQueries: [
      {
        query: GetEndpointDocument,
        variables: {
          packageVersionUuid: ctx?.state.packageVersionUuid,
          uuid: ctx?.state.uuid
        }
      }
    ]
  });
  if (saveError) {
    // eslint-disable-next-line no-console
    console.error('Error updating endpointSchema ', saveError);
  }

  const saveEndpointConfig = () => {
    if (params?.entityId && ctx?.state) {
      // remove package version uuid from schema
      const schema = { ...ctx.state.schema };

      updateEndpointSchema({
        variables: {
          packageVersionUuid: params.versionUuid,
          uuid: params.entityId,
          schema
        },
        notifyOnNetworkStatusChange: true
      }).then(() => setIsSaved(true));
    }
  };

  const cancelChange = () => {
    if (endpointData?.getEndpoint.schema) {
      ctx?.dispatch({
        type: EndpointActionTypes.INIT_ENDPOINT_CONFIG,
        payload: {
          schema: endpointData.getEndpoint.schema,
          service: {
            name: endpointData.getEndpoint.WebService.name,
            description: endpointData.getEndpoint.WebService.description,
            schema: endpointData.getEndpoint.WebService.schema
          },
          uuid: endpointData.getEndpoint.uuid,
          packageVersionUuid: endpointData.getEndpoint.packageVersionUuid
        }
      });
    }
  };

  // Ugly set of uuid && packageVersionUUid, it is in the state but should not be in the schema
  const renderCondition =
    !isSaved && endpointData?.getEndpoint.schema && !isEqual(endpointData?.getEndpoint.schema, ctx?.state.schema);

  return (isSaving || renderCondition) && isEditable ? (
    <SaveCancelWidget onCancel={cancelChange} onSave={saveEndpointConfig} isSaving={isSaving} />
  ) : null;
};
